/**
 * Refine Data Provider
 * API 통신을 위한 데이터 프로바이더
 */

import { DataProvider } from '@refinedev/core';
import axios, { AxiosInstance } from 'axios';
import { Dispatch, DISPATCH_STATUS_CODE_TO_ID } from '../pages/dispatch/types/dispatch.types';
import { MAIN_AUTH_CONFIG } from '../config/auth';

// API URL 설정 - /api 접두사가 없으면 자동으로 추가
const rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5714';
const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`;

// Axios 인스턴스 생성
export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request 인터셉터: 토큰 추가
axiosInstance.interceptors.request.use(
  config => {
    // localStorage와 sessionStorage 모두 확인
    const token =
      localStorage.getItem(MAIN_AUTH_CONFIG.tokenKey) ||
      sessionStorage.getItem(MAIN_AUTH_CONFIG.tokenKey);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response 인터셉터: 에러 처리
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // 401 Unauthorized 처리
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // 리프레시 토큰으로 새 액세스 토큰 요청
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data;
          localStorage.setItem(MAIN_AUTH_CONFIG.tokenKey, accessToken);

          // 원래 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // 리프레시 실패 시 로그아웃
        localStorage.removeItem(MAIN_AUTH_CONFIG.tokenKey);
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * 백엔드 DTO를 프론트엔드 Dispatch 타입으로 변환
 * 백엔드 Spring Boot API의 DispatchResponseDto를 프론트엔드 형식으로 매핑
 */
function transformDispatchResponse(dto: any): Dispatch {
  return {
    id: dto.id,
    dispatchNo: dto.bol || '', // bol → dispatchNo
    bol: dto.bol || '', // BOL 추가
    dispatchDate: dto.requestedDeliveryDate || dto.dispatchCompletedDatetime || '', // 배송요청일
    dispatchDatetime: dto.dispatchCompletedDatetime || '', // 배차일시
    requestedDeliveryDate: dto.requestedDeliveryDate || '', // 배송요청일
    status: dto.dispatchStatusId || '',
    statusName: dto.dispatchStatusName || '', // 배차상태명
    pickupStatusId: dto.pickupStatusId, // 픽업상태 ID (숫자)
    pickupStatus: dto.pickupStatusId || '', // 픽업상태 ID (하위 호환성)
    pickupStatusName: dto.pickupStatusName || '', // 픽업상태명
    deliveryStatusId: dto.deliveryStatusId, // 배송상태 ID (숫자)
    deliveryStatus: dto.deliveryStatusId || '', // 배송상태 ID (하위 호환성)
    deliveryStatusName: dto.deliveryStatusName || '', // 배송상태명
    driverName: dto.driverName || '',
    driverId: dto.driverId,
    driverType: dto.driverType || '', // 기사구분 코드
    driverTypeName: dto.driverTypeName || '', // 기사구분명
    driverPhone: dto.driverPhone || '', // 기사 연락처
    truckNo: dto.truckNumber || '', // truckNumber → truckNo
    truckId: dto.truckId,
    truckType: dto.truckType || '', // 차량종류 (ENUM 코드)
    truckTypeName: dto.truckTypeName || '', // 차량종류명 (영문)
    departure: dto.origin?.addressName || '', // 중첩 객체에서 주소명 추출
    destination: dto.destination?.addressName || '', // 중첩 객체에서 주소명 추출
    customerContactPerson: dto.customerContactPerson || '', // 고객 담당자
    customerContactPhone: dto.customerContactPhone || '', // 담당자 연락처
    cargoType: dto.cargoType || '', // 화물 종류
    cargoWeight: dto.cargoWeight || null,
    freight: dto.freight || null,
    remarks: dto.remarks || '',
    dispatchCancelDatetime: dto.dispatchCancelDatetime || '', // 배차취소일시
    createdAt: dto.createdAt || '',
    updatedAt: dto.updatedAt || '',
    createdBy: dto.createdBy,
    createdByName: dto.createdByName || '', // 등록자명
    updatedBy: dto.updatedBy,
    workplaceName: dto.workplaceName || '', // 근무처명
  };
}

/**
 * 백엔드 DTO를 프론트엔드 PickupManagement 타입으로 변환
 * 중첩 객체 구조를 평탄화하여 프론트엔드 형식으로 매핑
 */
function transformPickupResponse(dto: any): any {
  return {
    id: dto.id,
    dispatchId: dto.dispatchId,
    bol: dto.bol || '',
    trackingNumber: dto.trackingNumber || '',
    pickupStatusId: dto.pickupStatusId || 3,
    pickupStatusName: dto.pickupStatusName || '픽업대기',
    requestedDeliveryDate: dto.requestedDeliveryDate || '',
    actualPickupDatetime: dto.actualPickupDatetime || '',
    deliveryCondition: dto.deliveryCondition || '',
    driverId: dto.driverId,
    driverName: dto.driverName || '',
    driverPhone: dto.driverPhone || '',
    truckId: dto.truckId,
    truckNumber: dto.truckNumber || '',
    originAddressId: dto.originAddressId,
    originAddressName: dto.originAddressName || dto.origin?.addressName || '',
    originDetailAddress: dto.originDetailAddress || '',
    originPersonInCharge: dto.originPersonInCharge || '',
    originContactNumber: dto.originContactNumber || '',
    destinationAddressId: dto.destinationAddressId,
    destinationAddressName: dto.destinationAddressName || dto.destination?.addressName || '',
    destinationDetailAddress: dto.destinationDetailAddress || '',
    destinationPersonInCharge: dto.destinationPersonInCharge || '',
    destinationContactNumber: dto.destinationContactNumber || '',
    // 도착지 권역
    destinationMajorRegionName: dto.destinationMajorRegionName || '',
    destinationMinorRegionName: dto.destinationMinorRegionName || '',
    // 출발지 권역
    originMajorRegionName: dto.originMajorRegionName || '',
    originMinorRegionName: dto.originMinorRegionName || '',
    // Legacy fields (backward compatibility)
    majorRegionName: dto.destinationMajorRegionName || dto.majorRegionName || '',
    minorRegionName: dto.destinationMinorRegionName || dto.minorRegionName || '',
    shippingPersonName: dto.shippingPersonName || '',
    shippingPersonContact: dto.shippingPersonContact || '',
    receiverName: dto.receiverName || '',
    receiverContact: dto.receiverContact || '',
    createdBy: dto.createdBy,
    creatorName: dto.creatorName || '',
    workplaceName: dto.workplaceName || '',
    createdAt: dto.createdAt || '',
    updatedAt: dto.updatedAt || '',
    // EDI 204 L3 Summary 데이터 (중량/단위/부피/팔레트)
    totalWeight: dto.totalWeight || null,
    totalWeightUom: dto.totalWeightUom ?? null,
    totalVolume: dto.totalVolume ?? null,
    totalVolumeUom: dto.totalVolumeUom ?? null,
    totalPallets: dto.totalPallets || null,
  };
}

/**
 * 백엔드 DTO를 프론트엔드 DeliveryManagement 타입으로 변환
 * 중첩 객체 구조를 평탄화하여 프론트엔드 형식으로 매핑
 */
function transformDeliveryResponse(dto: any): any {
  return {
    id: dto.id,
    pickupManagementId: dto.pickupManagementId,
    dispatchId: dto.dispatchId,
    bol: dto.bol || '',
    trackingNumber: dto.trackingNumber || '',
    pickupStatusId: dto.pickupStatusId,
    pickupStatusName: dto.pickupStatusName || '',
    deliveryStatusId: dto.deliveryStatusId || 1,
    deliveryStatusName: dto.deliveryStatusName || '배송대기',
    requestedDeliveryDate: dto.requestedDeliveryDate || '',
    actualPickupDatetime: dto.actualPickupDatetime || '',
    // 완료일시: deliveryCompletedDatetime
    deliveryCompletedDatetime: dto.deliveryCompletedDatetime || '',
    deliveryCondition: dto.deliveryCondition || '',
    driverId: dto.driverId,
    driverName: dto.driverName || '',
    driverPhone: dto.driverPhone || '',
    truckId: dto.truckId,
    truckNumber: dto.truckNumber || '',
    // 출발지: origin.addressName
    originAddressId: dto.origin?.addressId || dto.originAddressId,
    originAddressName: dto.origin?.addressName || dto.originAddressName || '',
    // 도착지: destination.addressName
    destinationAddressId: dto.destination?.addressId || dto.destinationAddressId,
    destinationAddressName: dto.destination?.addressName || dto.destinationAddressName || '',
    // 대권역: destination.parentRegionName
    destinationMajorRegionName:
      dto.destination?.parentRegionName || dto.destinationMajorRegionName || '',
    // 중권역: destination.regionName
    destinationMinorRegionName: dto.destination?.regionName || dto.destinationMinorRegionName || '',
    // 인수자: customerContactPerson
    receiverName:
      dto.customerContactPerson || dto.receiverName || dto.destinationPersonInCharge || '',
    // 인수자 연락처: customerContactPhone
    receiverContact:
      dto.customerContactPhone || dto.receiverContact || dto.destinationContactNumber || '',
    // 고객 담당자: driverName (grid 컬럼명과 매핑)
    shippingPersonName: dto.driverName || dto.shippingPersonName || '',
    // 담당자 연락처: driverPhone (grid 컬럼명과 매핑)
    shippingPersonContact: dto.driverPhone || dto.shippingPersonContact || '',
    destinationPersonInCharge: dto.destinationPersonInCharge || '',
    destinationContactNumber: dto.destinationContactNumber || '',
    createdBy: dto.createdBy,
    // 등록자: createdByName
    creatorName: dto.createdByName || dto.creatorName || '',
    workplaceName: dto.workplaceName || '',
    deliveryNote: dto.deliveryNote || '',
    createdAt: dto.createdAt || '',
    updatedAt: dto.updatedAt || '',
  };
}

/**
 * 백엔드 DTO를 프론트엔드 TransmissionRow 타입으로 변환 (BOL당 1행 컬럼형)
 * transmission-list-stage-status — 화이트리스트 명시적 변환
 * 메모리 dataprovider-whitelist-transform 준수: 필드 목록 명시, 신규 필드 추가 시 여기도 갱신
 *
 * 화이트리스트 필드 (TransmissionRow):
 *   bol, transmissionId, deliveryStatusName, requestedDeliveryDate,
 *   pickupDestinationAddress, deliveryDestinationAddress,
 *   majorRegion, minorRegion, receiverName, receiverContact, stages[]
 *
 * 화이트리스트 필드 (TransmissionStage):
 *   stageCode, stageName, status, transmittedAt, fileName, ackStatus
 */
function transformTransmissionResponse(dto: any): any {
  const VALID_STAGE_CODES = ['XB', 'AF', 'X2', 'D1', 'CD'] as const;
  type ValidStageCode = (typeof VALID_STAGE_CODES)[number];

  const stages = Array.isArray(dto.stages)
    ? dto.stages.map((stage: any) => ({
        stageCode: (VALID_STAGE_CODES as readonly string[]).includes(stage.stageCode)
          ? (stage.stageCode as ValidStageCode)
          : stage.stageCode,
        stageName: stage.stageName ?? '',
        status: stage.status ?? null,
        transmittedAt: stage.transmittedAt ?? null,
        fileName: stage.fileName ?? null,
        ackStatus: stage.ackStatus ?? null,
      }))
    : [];

  return {
    bol: dto.bol ?? '',
    transmissionId: dto.transmissionId ?? null,
    deliveryStatusName: dto.deliveryStatusName ?? '',
    requestedDeliveryDate: dto.requestedDeliveryDate ?? null,
    pickupDestinationAddress: dto.pickupDestinationAddress ?? null,
    deliveryDestinationAddress: dto.deliveryDestinationAddress ?? null,
    majorRegion: dto.majorRegion ?? null,
    minorRegion: dto.minorRegion ?? null,
    receiverName: dto.receiverName ?? null,
    receiverContact: dto.receiverContact ?? null,
    stages,
  };
}

/**
 * 백엔드 DTO를 프론트엔드 DeliveryStatus 타입으로 변환
 * origin/destination 중첩 객체 구조를 그대로 유지
 */
function transformDeliveryStatusResponse(dto: any): any {
  return {
    pickupManagementId: dto.pickupManagementId,
    deliveryManagementId: dto.deliveryManagementId,
    transmissionManagementId: dto.transmissionManagementId,
    dispatchId: dto.dispatchId,
    bol: dto.bol || '',
    trackingNumber: dto.trackingNumber || '',
    // 수량/중량 정보
    totalQuantity: dto.totalQuantity || null,
    totalWeight: dto.totalWeight || null,
    dispatchStatusId: dto.dispatchStatusId,
    dispatchStatusName: dto.dispatchStatusName || '',
    pickupStatusId: dto.pickupStatusId,
    pickupStatusName: dto.pickupStatusName || '',
    deliveryStatusId: dto.deliveryStatusId,
    deliveryStatusName: dto.deliveryStatusName || '',
    transmissionStatusId: dto.transmissionStatusId,
    transmissionStatusName: dto.transmissionStatusName || '',
    requestedDeliveryDate: dto.requestedDeliveryDate || '',
    dispatchCompletedDatetime: dto.dispatchCompletedDatetime || '',
    pickupDatetime: dto.pickupDatetime || '',
    deliveryDatetime: dto.deliveryDatetime || '',
    transmissionDatetime: dto.transmissionDatetime || '',
    // EDI 단계별 외부 SFTP 전송 완료 시각 (접수 XB / 도착예정 X2 / 배송완료출발 CD)
    orderAcceptedAt: dto.orderAcceptedAt || '',
    arrivalEstimateAt: dto.arrivalEstimateAt || '',
    departedAt: dto.departedAt || '',
    // EDI 204 G62 qualifier 54 도착지 ship datetime (204 미수신 시 빈값)
    shipDateTime: dto.shipDateTime || '',
    // Company = 도착지 회사명(destination.addressName) 평탄화 — 그리드 컬럼 직접 표시용
    company: dto.destination?.addressName || '',
    deliveryCondition: dto.deliveryCondition || '',
    // origin/destination 중첩 객체 그대로 전달
    origin: dto.origin || null,
    destination: dto.destination || null,
    pickupReceiverName: dto.pickupReceiverName || '',
    pickupReceiverContact: dto.pickupReceiverContact || '',
    deliveryReceiverName: dto.deliveryReceiverName || '',
    deliveryReceiverContact: dto.deliveryReceiverContact || '',
    driverType: dto.driverType || '',
    driverTypeName: dto.driverTypeName || '',
    driverName: dto.driverName || '',
    driverPhone: dto.driverPhone || '',
    truckNumber: dto.truckNumber || '',
    truckType: dto.truckType || '',
    truckTypeName: dto.truckTypeName || '',
    createdAt: dto.createdAt || '',
    updatedAt: dto.updatedAt || '',
    // 등록자 / 등록자 근무처
    creatorName: dto.creatorName || '',
    workplaceName: dto.workplaceName || '',
  };
}

export const dataProvider: DataProvider = {
  // 목록 조회
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const url = `/${resource}`;
    const { current = 1, pageSize = 30 } = pagination ?? {};

    // push-notification-logs 리소스 전용 처리
    if (resource === 'push-notification-logs') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && operator === 'gte') {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && operator === 'lte') {
              params.endDate = value;
            }
            // 발송 구분 필터
            else if (field === 'notificationType' && operator === 'eq') {
              params.notificationType = value;
            }
            // 키워드 검색 (기사명 또는 연락처)
            else if (field === 'keyword' && operator === 'contains') {
              params.keyword = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // Spring Page 응답 파싱
      return {
        data: data.content || [],
        total: data.totalElements || 0,
      };
    }

    // pickup-management 리소스 전용 처리
    if (resource === 'pickup-management') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && operator === 'gte') {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && operator === 'lte') {
              params.endDate = value;
            }
            // 픽업 상태 필터
            else if (field === 'pickupStatusId' && operator === 'eq') {
              params.pickupStatusId = value;
            }
            // 키워드 검색
            else if (field === 'keyword' && operator === 'contains') {
              params.keyword = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // Spring Page 응답 파싱 및 DTO 변환
      return {
        data: (data.content || []).map(transformPickupResponse),
        total: data.totalElements || 0,
      };
    }

    // delivery-management 리소스 전용 처리
    if (resource === 'delivery-management') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && (operator === 'gte' || operator === 'eq')) {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && (operator === 'lte' || operator === 'eq')) {
              params.endDate = value;
            }
            // 배송 상태 필터
            else if (field === 'deliveryStatusId' && operator === 'eq') {
              params.deliveryStatusId = value;
            }
            // 키워드 검색
            else if (field === 'keyword' && operator === 'contains') {
              params.keyword = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // 🔍 디버깅: API 응답 데이터 구조 확인
      if (data.content && data.content.length > 0) {
        console.log('🔍 [delivery-management] First item structure:', data.content[0]);
      }

      // Spring Page 응답 파싱 및 DTO 변환 (pickup-management와 동일한 구조)
      return {
        data: (data.content || []).map(transformDeliveryResponse),
        total: data.totalElements || 0,
      };
    }

    // transmission-management 리소스 전용 처리
    if (resource === 'transmission-management') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && operator === 'eq') {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && operator === 'eq') {
              params.endDate = value;
            }
            // 전송 상태 필터 (형식: "typeId-statusId" 예: "1-1", "1-2", "2-1", "2-2")
            else if (field === 'transmissionStatus' && operator === 'eq') {
              params.transmissionStatus = value;
            }
            // 키워드 검색 (BOL, 추적번호, 기사명, 차량번호, 전화번호 통합 검색)
            else if (field === 'keyword' && operator === 'eq') {
              params.keyword = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // 응답 구조: { success: true, code: 200, data: { data: [], totalCount: 0 } }
      const responseData = data.data || data;
      return {
        data: (responseData.data || []).map(transformTransmissionResponse),
        total: responseData.totalCount || 0,
      };
    }

    // edoc-file-list 리소스 전용 처리
    // (E-DOC 수·발신 관리 — Applied 와 주고받는 공유 폴더 파일 교환 목록)
    if (resource === 'edoc-file-list') {
      const params: any = { page: current, pageSize: pageSize };

      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;
            if (operator !== 'eq' || value === undefined || value === null || value === '') {
              return;
            }
            if (['direction', 'keyword', 'startDate', 'endDate'].includes(field)) {
              params[field] = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get('/epod/files', { params });

      // 응답 구조: { data: [], total, page, pageSize, direction, basePath }
      // basePath(기준 폴더)는 화면 헤더에 그대로 쓰므로 함께 넘긴다.
      return {
        data: data.data || [],
        total: data.total || 0,
        basePath: data.basePath,
      } as any;
    }

    // epod-pod-list 리소스 전용 처리 (E-POD 목록 — 배송 건 + POD 문서, 미매칭 POD 포함)
    if (resource === 'epod-pod-list') {
      const params: any = { page: current, pageSize: pageSize };

      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;
            if (operator !== 'eq' || value === undefined || value === null || value === '') {
              return;
            }
            if (['startDate', 'endDate', 'keyword', 'matchFilter'].includes(field)) {
              params[field] = value;
            }
          }
        });
      }

      // 정렬 — 머리글에서 고른 기준(등록일·배송완료일시). 없으면 서버 기본값(등록일 최신순)
      if (sorters && sorters.length > 0) {
        params.sortField = sorters[0].field;
        params.sortOrder = sorters[0].order;
      }

      const { data } = await axiosInstance.get('/epod/scan/pod-list', { params });

      return {
        data: data.items || [],
        total: data.total || 0,
      };
    }

    // epod-scan-jobs 리소스 전용 처리 (복합기 스캔 PDF 파싱 작업)
    // 위 epod-*-list 와 달리 DB 기반이라 응답 키가 items 다.
    if (resource === 'epod-scan-jobs') {
      const params: any = {
        page: current,
        pageSize: pageSize,
      };

      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;
            if (operator !== 'eq' || value === undefined || value === null || value === '') {
              return;
            }
            if (['startDate', 'endDate', 'keyword', 'status', 'uploadSource'].includes(field)) {
              params[field] = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get('/epod/scan/jobs', { params });

      return {
        data: data.items || [],
        total: data.total || 0,
      };
    }

    // delivery-status 리소스 전용 처리
    if (resource === 'delivery-status') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // meta에서 language 파라미터 추출
      if (meta?.language) {
        params.language = meta.language;
      }

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && operator === 'eq') {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && operator === 'eq') {
              params.endDate = value;
            }
            // 배차상태 필터
            else if (field === 'dispatchStatus' && operator === 'eq') {
              params.dispatchStatus = value;
            }
            // 픽업상태 필터
            else if (field === 'pickupStatus' && operator === 'eq') {
              params.pickupStatus = value;
            }
            // 배송상태 필터
            else if (field === 'deliveryStatus' && operator === 'eq') {
              params.deliveryStatus = value;
            }
            // 전송상태 필터
            else if (field === 'transmissionStatus' && operator === 'eq') {
              params.transmissionStatus = value;
            }
            // 키워드 검색
            else if (field === 'keyword' && operator === 'eq') {
              params.keyword = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // 응답 구조: { data: { data: [], totalCount: 0 } }
      const responseData = data.data || data;
      return {
        data: (responseData.data || []).map(transformDeliveryStatusResponse),
        total: responseData.totalCount || 0,
      };
    }

    // dispatches 리소스 전용 처리
    if (resource === 'dispatches') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize, // Spring은 'size' 파라미터 사용
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 날짜 범위 필터 (dispatchDate → startDate/endDate)
            if (field === 'dispatchDate') {
              if (operator === 'gte') {
                params.startDate = value;
              } else if (operator === 'lte') {
                params.endDate = value;
              }
            }
            // 배차 상태 필터 (status → dispatchStatusId)
            // 백엔드는 Integer ID를 요구하므로 status code를 ID로 변환
            else if (field === 'status' && operator === 'eq') {
              const statusId = DISPATCH_STATUS_CODE_TO_ID[value];
              if (statusId !== undefined) {
                params.dispatchStatusId = statusId;
              }
            }
            // 기사 필터
            else if (field === 'driverId' && operator === 'eq') {
              params.driverId = value;
            }
            // 키워드 검색
            else if (field === 'keyword' && operator === 'contains') {
              params.keyword = value;
            }
          }
        });
      }

      // 정렬 처리
      if (sorters && sorters.length > 0) {
        const sorter = sorters[0];
        params.sort = `${sorter.field},${sorter.order === 'asc' ? 'asc' : 'desc'}`;
      }

      const { data } = await axiosInstance.get(url, { params });

      // Spring Page 응답 파싱 및 DTO 변환
      return {
        data: (data.content || []).map(transformDispatchResponse),
        total: data.totalElements || 0,
      };
    }

    // logs/edi-scheduler 리소스 전용 처리
    if (resource === 'logs/edi-scheduler') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && (operator === 'gte' || operator === 'eq')) {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && (operator === 'lte' || operator === 'eq')) {
              params.endDate = value;
            }
            // EDI 유형 필터
            else if (field === 'ediType' && operator === 'eq') {
              params.ediType = value;
            }
            // 처리 상태 필터
            else if (field === 'status' && operator === 'eq') {
              params.status = value;
            }
            // 파일명 부분검색
            else if (field === 'keyword' && operator === 'contains') {
              params.keyword = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // API 응답 구조: { success: true, code: 200, data: { content: [], totalElements: 0, ... } }
      const responseData = data.data || data;

      return {
        data: responseData.content || [],
        total: responseData.totalElements || 0,
      };
    }

    // logs/edi204 리소스 전용 처리 (EDI204 수신 로그 + 997 생성/송신 현황)
    if (resource === 'logs/edi204') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;
            if (field === 'startDate' && (operator === 'gte' || operator === 'eq')) {
              params.startDate = value;
            } else if (field === 'endDate' && (operator === 'lte' || operator === 'eq')) {
              params.endDate = value;
            } else if (field === 'status' && operator === 'eq') {
              params.status = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });
      const responseData = data.data || data;

      return {
        data: responseData.content || [],
        total: responseData.totalElements || 0,
      };
    }

    // excel-upload-logs 리소스 전용 처리 (엑셀 전체업로드 로그)
    if (resource === 'excel-upload-logs') {
      const params: any = {
        page: current - 1, // Spring 0-based
        size: pageSize,
      };

      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;
            if (field === 'startDate' && (operator === 'gte' || operator === 'eq')) {
              params.startDate = value;
            } else if (field === 'endDate' && (operator === 'lte' || operator === 'eq')) {
              params.endDate = value;
            } else if (field === 'fileName' && (operator === 'contains' || operator === 'eq')) {
              params.fileName = value;
            } else if (field === 'status' && operator === 'eq') {
              params.status = value;
            } else if (field === 'uploadType' && operator === 'eq') {
              params.uploadType = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });
      const responseData = data.data || data;

      return {
        data: responseData.content || [],
        total: responseData.totalElements || 0,
      };
    }

    // logs/sms, logs/push, logs/system 리소스 전용 처리
    if (resource === 'logs/sms' || resource === 'logs/push' || resource === 'logs/system') {
      const params: any = {
        page: current - 1, // Spring Pageable은 0부터 시작
        size: pageSize,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, operator, value } = filter;

            // 시작일 필터
            if (field === 'startDate' && (operator === 'gte' || operator === 'eq')) {
              params.startDate = value;
            }
            // 종료일 필터
            else if (field === 'endDate' && (operator === 'lte' || operator === 'eq')) {
              params.endDate = value;
            }
            // 상태 필터
            else if (field === 'status' && operator === 'eq') {
              params.status = value;
            }
            // 키워드 검색
            else if (field === 'keyword' && operator === 'contains') {
              params.keyword = value;
            }
            // 작업 유형 필터 (SYSTEM 로그용)
            else if (field === 'actionType' && operator === 'eq') {
              params.actionType = value;
            }
          }
        });
      }

      const { data } = await axiosInstance.get(url, { params });

      // API 응답 구조: { success: true, code: 200, data: { content: [], totalElements: 0, ... } }
      const responseData = data.data || data;

      // 🔍 디버깅: logs API 응답 데이터 구조 확인
      console.log(`🔍 [${resource}] API 응답:`, data);
      console.log(`🔍 [${resource}] responseData:`, responseData);
      if (responseData.content && responseData.content.length > 0) {
        console.log(`🔍 [${resource}] First item:`, responseData.content[0]);
      }

      return {
        data: responseData.content || [],
        total: responseData.totalElements || 0,
      };
    }

    // settlement 리소스 전용 처리
    if (resource === 'settlement') {
      const params: any = {
        page: (pagination?.current ?? 1) - 1, // Spring Pageable은 0부터 시작
        size: pagination?.pageSize ?? 30,
      };

      // 필터 파라미터 매핑
      if (filters) {
        filters.forEach(filter => {
          if ('field' in filter) {
            const { field, value } = filter;
            if (field === 'startDate') {
              if (value !== '' && value != null) params.startDate = value;
            } else if (field === 'endDate') {
              if (value !== '' && value != null) params.endDate = value;
            } else if (field === 'keyword') {
              if (value !== '' && value != null) params.keyword = value;
            } else {
              // truckType / serviceLevel / sapOrderType / outOfGauge / dangerousGoods
              if (value !== '' && value != null) params[field] = value;
            }
          }
        });
      }

      // 정렬 처리 (단일 컬럼)
      if (sorters && sorters.length > 0) {
        params.sort = `${sorters[0].field},${sorters[0].order}`;
      }

      const { data } = await axiosInstance.get('/settlement', { params });
      const rd = data.data || data;

      return {
        data: rd.content || [],
        total: rd.totalElements || 0,
      };
    }

    // 기본 처리 (다른 리소스)
    const params: any = {
      page: current,
      pageSize,
    };

    // 필터 처리
    if (filters) {
      filters.forEach(filter => {
        if ('field' in filter) {
          const { field, operator, value } = filter;

          if (operator === 'eq') {
            params[field] = value;
          } else if (operator === 'contains') {
            params[field] = value;
          } else if (operator === 'gte') {
            params[field] = value;
          } else if (operator === 'lte') {
            params[field] = value;
          }
        }
      });
    }

    // 정렬 처리
    if (sorters && sorters.length > 0) {
      const sorter = sorters[0];
      params.sortField = sorter.field;
      params.sortOrder = sorter.order;
    }

    const { data } = await axiosInstance.get(url, { params });

    return {
      data: data.data || [],
      total: data.total || 0,
    };
  },

  // 단일 항목 조회
  getOne: async ({ resource, id, meta }) => {
    const url = `/${resource}/${id}`;
    const { data } = await axiosInstance.get(url);

    // API 응답이 { success, code, data } 형태인 경우 data 추출
    const actualData = data.data || data;

    // dispatches 리소스의 경우 DTO 변환
    if (resource === 'dispatches') {
      return {
        data: transformDispatchResponse(actualData),
      };
    }

    // pickup-management 리소스의 경우 DTO 변환
    if (resource === 'pickup-management') {
      return {
        data: transformPickupResponse(actualData),
      };
    }

    // delivery-management 리소스의 경우 DTO 변환
    if (resource === 'delivery-management') {
      return {
        data: transformDeliveryResponse(actualData),
      };
    }

    // transmission-management 리소스의 경우 DTO 변환
    if (resource === 'transmission-management') {
      return {
        data: transformTransmissionResponse(actualData),
      };
    }

    return {
      data: actualData,
    };
  },

  // 생성
  create: async ({ resource, variables, meta }) => {
    const url = `/${resource}`;
    const { data } = await axiosInstance.post(url, variables);

    return {
      data: data.data || data,
    };
  },

  // 수정
  update: async ({ resource, id, variables, meta }) => {
    const url = `/${resource}/${id}`;
    const { data } = await axiosInstance.put(url, variables);

    return {
      data: data.data || data,
    };
  },

  // 삭제
  deleteOne: async ({ resource, id, meta }) => {
    const url = `/${resource}/${id}`;
    const { data } = await axiosInstance.delete(url);

    return {
      data: data.data || data,
    };
  },

  // API URL 반환
  getApiUrl: () => API_URL,

  // 커스텀 메서드 (선택사항)
  // axiosInstance는 이미 baseURL(API_URL)이 설정되어 있으므로 상대 경로만 전달
  custom: async ({ url, method, filters, sorters, payload, query, headers }) => {
    const { data } = await axiosInstance({
      url: url,
      method,
      data: payload,
      params: query,
      headers,
    });

    // API 응답이 CommonRes 구조인 경우 data 필드를 추출하여 반환
    // CommonRes 구조: { success: true, code: 200, data: {...} }
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return { data: data.data };
    }

    return { data };
  },
};

export default dataProvider;
